import { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, PaginationMeta } from '@/utils/apiClient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface PaginationControlsProps {
  pagination: PaginationMeta;
  /** Original markdown content (page 1) from the LLM response */
  originalContent: string;
}

/**
 * Pagination controls for large result sets in chat messages.
 *
 * When the API returns more items than can fit in the LLM context,
 * the backend creates a pagination context and injects metadata.
 * This component shows Previous/Next controls and fetches additional
 * pages directly from the cluster API (no LLM involved = fast).
 */
export function PaginationControls({ pagination, originalContent }: PaginationControlsProps) {
  const { t, language } = useTranslation();
  const [currentPage, setCurrentPage] = useState(pagination.current_page);
  const [loading, setLoading] = useState(false);
  const [pageContents, setPageContents] = useState<Record<number, string>>({
    [pagination.current_page]: originalContent,
  });
  const [error, setError] = useState<string | null>(null);

  const [expired, setExpired] = useState(false);

  const fetchPage = useCallback(async (page: number) => {
    // Return cached page if available
    if (pageContents[page]) {
      setCurrentPage(page);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.fetchPage(pagination.pagination_id, page);

      // Check if the pagination context has expired
      if (data && (data as any).expired) {
        setExpired(true);
        setError(null);
        return;
      }

      // Format items as a markdown table (matching the LLM's format)
      const items = data.items || [];
      let content: string;

      if (items.length === 0) {
        content = '*No items on this page.*';
      } else if (typeof items[0] === 'object' && items[0] !== null) {
        // Build a markdown table from the items
        const keys = Object.keys(items[0]).filter(k => !k.startsWith('_'));
        const header = '| ' + keys.join(' | ') + ' |';
        const divider = '| ' + keys.map(() => '---').join(' | ') + ' |';
        const rows = items.map((item: Record<string, unknown>) =>
          '| ' + keys.map(k => {
            const val = item[k];
            if (val === null || val === undefined) return '';
            if (typeof val === 'object') return JSON.stringify(val).slice(0, 50);
            return String(val).slice(0, 80);
          }).join(' | ') + ' |'
        );
        content = [header, divider, ...rows].join('\n');
      } else {
        // Simple list
        content = items.map((item: unknown, i: number) => `${i + 1}. ${String(item)}`).join('\n');
      }

      setPageContents(prev => ({ ...prev, [page]: content }));
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch page');
    } finally {
      setLoading(false);
    }
  }, [pagination.pagination_id, pageContents]);

  const pageLabel = t.common?.page || 'Page';
  const ofLabel = t.common?.of || 'of';

  if (expired) {
    return (
      <div className="mt-3">
        <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            {t.pagination?.expired ?? 'Results expired'}
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
            {t.pagination?.expiredHint ?? 'Please re-run your query to get fresh results.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {/* Page content */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500 mr-2" />
          <span className="text-sm text-gray-500">
            {t.common?.loading || 'Loading'} {pageLabel.toLowerCase()} {currentPage}...
          </span>
        </div>
      ) : pageContents[currentPage] && currentPage > 1 ? (
        <div className="prose dark:prose-invert max-w-none text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {pageContents[currentPage]}
          </ReactMarkdown>
        </div>
      ) : null}

      {/* Error message */}
      {error && (
        <div className="text-sm text-red-500 mt-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded">
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-between mt-3 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => fetchPage(currentPage - 1)}
          disabled={currentPage <= 1 || loading}
          className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t.common?.previous || 'Previous'}
        </button>

        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <span>
            {pageLabel} {currentPage} {ofLabel} {pagination.total_pages}
          </span>
          <span className="text-gray-400 dark:text-gray-500">
            ({formatCount(language, pagination.total_count, t.common.items, pagination.total_count.toLocaleString())})
          </span>
        </div>

        <button
          onClick={() => fetchPage(currentPage + 1)}
          disabled={currentPage >= pagination.total_pages || loading}
          className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {t.common?.next || 'Next'}
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
}
